import {
  IntegrationDetails,
  IntegrationListing,
  IntegrationModel,
  IntegrationUser,
} from '@fullstory/proto/fs/services/emissary';
import { createLoadingReducer } from '@fullstory/redux-util';
import { combineReducers } from '@fullstory/redux-util/utils';
import { IntegrationsActions } from './actions';
import { AuthTokenStatuses, IntegrationsRequestTypes } from './types';

const availableIntegrations = (
  state: IntegrationListing[] = [],
  action: IntegrationsActions,
): IntegrationListing[] => {
  switch (action.type) {
    case IntegrationsRequestTypes.GetAvailableIntegrationsSuccess:
      return action.data.integrations;
    default:
      return state;
  }
};

const installedIntegrations = (
  state: IntegrationModel[] = [],
  action: IntegrationsActions,
): IntegrationModel[] => {
  switch (action.type) {
    case IntegrationsRequestTypes.GetInstalledIntegrationsSuccess:
      return action.data.models;
    case IntegrationsRequestTypes.DeleteIntegrationConfigSuccess:
      return state.map(i => {
        const idx = i.configs.findIndex(c => c.id === action.request.body?.configId);

        return idx === -1
          ? i
          : { ...i, configs: [...i.configs.slice(0, idx), ...i.configs.slice(idx + 1)] };
      });
    default:
      return state;
  }
};

const integrationDetails = (
  state: IntegrationDetails = <IntegrationDetails>{},
  action: IntegrationsActions,
): IntegrationDetails => {
  switch (action.type) {
    case IntegrationsRequestTypes.GetIntegrationDetailsSuccess:
      return action.data;
    case IntegrationsRequestTypes.GetIntegrationDetailsByProviderIdSuccess:
      return action.data;
    case IntegrationsRequestTypes.GetIntegrationDetailsByProviderIdFailure:
      return <IntegrationDetails>{};
    default:
      return state;
  }
};

const authTokenStatuses = (
  state: AuthTokenStatuses = {},
  action: IntegrationsActions,
): AuthTokenStatuses => {
  switch (action.type) {
    case IntegrationsRequestTypes.GetAuthTokenStatusesSuccess:
      return action.data.tokenStatuses;
    default:
      return state;
  }
};

const integrationUsers = (
  state: IntegrationUser[] = [],
  action: IntegrationsActions,
): IntegrationUser[] => {
  switch (action.type) {
    case IntegrationsRequestTypes.GetIntegrationUsersSuccess:
      return action.data.users;
    default:
      return state;
  }
};

// Used for the Slack alert configuration in UserTrendAlerts
const getIntegrationDetailsLoading = createLoadingReducer(
  IntegrationsRequestTypes.GetIntegrationDetailsByProviderIdRequest,
  IntegrationsRequestTypes.GetIntegrationDetailsByProviderIdSuccess,
  IntegrationsRequestTypes.GetIntegrationDetailsByProviderIdFailure,
);

type IntegrationsReducerState = {
  availableIntegrations: ReturnType<typeof availableIntegrations>;
  installedIntegrations: ReturnType<typeof installedIntegrations>;
  integrationDetails: ReturnType<typeof integrationDetails>;
  integrationUsers: ReturnType<typeof integrationUsers>;
  authTokenStatuses: ReturnType<typeof authTokenStatuses>;
  getIntegrationDetailsLoading: ReturnType<typeof getIntegrationDetailsLoading>;
};

export const integrationsReducer = combineReducers<IntegrationsReducerState>({
  availableIntegrations,
  installedIntegrations,
  integrationDetails,
  integrationUsers,
  authTokenStatuses,
  getIntegrationDetailsLoading,
});

export type integrationsReducer = ReturnType<typeof integrationsReducer>;
